import { type useUser } from "@clerk/nextjs";
import { User } from "@prisma/client";

import { Avatar, AvatarProps } from "~/ui";

import styles from "./UserAvatar.module.css";

type UserResource = NonNullable<ReturnType<typeof useUser>["user"]>;
type BaseUser = Pick<User, "firstName" | "lastName" | "emailAddress" | "profileImageUrl"> | UserResource;

export interface UserAvatarProps<U extends BaseUser> extends Omit<AvatarProps, "src" | "h" | "w" | "style"> {
  readonly user?: U | null;
}

const userIsResource = (user: BaseUser): user is UserResource => "emailAddresses" in user && Array.isArray(user.emailAddresses);

/** A Prisma `User` stores the avatar as `profileImageUrl`; a Clerk `UserResource` exposes `imageUrl`. */
const avatarUrl = (user: BaseUser): string | null => (userIsResource(user) ? user.imageUrl : user.profileImageUrl);

export const UserAvatar = <U extends BaseUser>({ user, className, ...props }: UserAvatarProps<U>): JSX.Element =>
  user ? (
    <Avatar
      {...props}
      className={className ? `${styles.userAvatar} ${className}` : styles.userAvatar}
      src={avatarUrl(user)}
      h="100%"
      w="auto"
      style={{ aspectRatio: 1 }}
    />
  ) : (
    <></>
  );
